import { Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getSelf, logoutUser } from './store/actions/auth-actions';
import { Roles } from './helpers/consts/roles';
import RoleGuard from './helpers/components/RoleGuard';
import PublicRoute from './helpers/components/PublicRoute';
import LandingPage from './helpers/components/LandingPage';
import PrivateRoute from './helpers/components/PrivateRoute';

import Login from './pages/Login.js';
import CarHouses from './pages/CarHouses';
import CarHouse from './pages/CarHouse';
import Users from './pages/Users';
import Sidebar from './components/Sidebar/Sidebar';
import Admins from './pages/Admins';
import Vehicles from './pages/Vehicles';
import Vehicle from './pages/Vehicle';
import UserProfile from './pages/UserProfile';
import CreditsHistory from './pages/CreditsHistory';
import Tags from './pages/Tags.js';
import VehicleSpecifications from './pages/VehicleSpecifications.js';
import SubscriptionsPage from './pages/SubscriptionsPage.js';
import Logos from './pages/Logos.js';
import BasicInformation from './pages/BasicInformation.js';
import PrivacyPolicy from './pages/PrivacyPolicy.js';
import TermsOfUse from './pages/TermsOfUse.js';
import ImageRequests from './pages/ImageRequests';

import './App.css';

function App() {
  const { isLoggedIn, loggedInUser, auth } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const isAuthenticated = (isLoggedIn && !!loggedInUser) || !isLoggedIn;

  useEffect(() => {
    const { token, _id } = auth;

    // if there is token and there is no user
    // if there is _id, get user otherwise logout
    if (token && !loggedInUser) {
      _id ? dispatch(getSelf(_id)) : dispatch(logoutUser());
    }
  }, [auth, dispatch, loggedInUser]);

  const rootRoutes = (
    <>
      <Route path="/car-houses" element={<CarHouses />}></Route>
      <Route path="/car-houses/:carhouseId" element={<CarHouse />}></Route>
      <Route path="/admins" element={<Admins />}></Route>
      <Route
        path="/car-houses/:carHouseId/credits"
        element={<CreditsHistory />}
      ></Route>
      <Route path="/tags" element={<Tags />}></Route>
      <Route
        path="/vehicle-specifications"
        element={<VehicleSpecifications />}
      ></Route>
      <Route
        path="/car-houses/:carHouseId/subscriptions"
        element={<SubscriptionsPage />}
      />
      <Route
        path="/car-houses/:carHouseId/users/:userId/subscriptions"
        element={<SubscriptionsPage />}
      />
      <Route
        path="/car-houses/:carHouseId/admins/:userId/subscriptions"
        element={<SubscriptionsPage />}
      />
      <Route path="/basic-information" element={<BasicInformation />}></Route>
    </>
  );

  const carHouseAdminRoutes = (
    <>
      <Route path="/users" element={<Users />}></Route>
      <Route path="/:carHouseId/credits" element={<CreditsHistory />}></Route>
      <Route
        path="/:carHouseId/subscriptions"
        element={<SubscriptionsPage />}
      ></Route>
    </>
  );

  const mainRoutes = (
    <>
      <Route path="/vehicles" element={<Vehicles />}></Route>
      <Route path="/vehicles/:vehicleId" element={<Vehicle />}></Route>
      <Route path="/profile" element={<UserProfile />}></Route>
      <Route path="/logos" element={<Logos />}></Route>
      <Route path="/purchase-vehicles" element={<Vehicles />}></Route>
      <Route path="/purchase-vehicles/:vehicleId" element={<Vehicle />}></Route>
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-use" element={<TermsOfUse />} />
      <Route path="/image-requests" element={<ImageRequests />}></Route>
    </>
  );

  return (
    <>
      {!isAuthenticated && <p>Loading........</p>}

      {isAuthenticated && (
        <>
          <Routes>
            {/* Landing page */}
            <Route path="/" exact element={<LandingPage />} />

            {/* Public routes */}
            <Route element={<PublicRoute />}>
              <Route path="/login" element={<Login />}></Route>
            </Route>

            {/* Private routes */}
            <Route element={<PrivateRoute />}>
              <Route element={<RoleGuard allowedRoles={[Roles.ROOT]} />}>
                <Route element={<Sidebar />}>{rootRoutes}</Route>
              </Route>

              <Route
                element={
                  <RoleGuard
                    allowedRoles={[
                      Roles.ROOT,
                      Roles.ADMIN,
                      Roles.CARHOUSEADMIN,
                      Roles.USER,
                    ]}
                  />
                }
              >
                <Route element={<Sidebar />}>{mainRoutes}</Route>
              </Route>

              <Route
                element={<RoleGuard allowedRoles={[Roles.CARHOUSEADMIN]} />}
              >
                <Route element={<Sidebar />}>{carHouseAdminRoutes}</Route>
              </Route>
            </Route>
          </Routes>
        </>
      )}
    </>
  );
}

export default App;
